import apiClient from './apiService';

export type RCABucket = 'People' | 'Process' | 'Technology' | 'Environment';

export interface CapaRootCause {
    id: number;
    entity_id: number;
    issue_id: number;
    investigation_id?: number | null;
    bucket: RCABucket;
    cause_description: string;
    code?: string | null;
    prev_record_id?: number | null;
    item_type_id?: number | null;
    item_record_id?: number | null;
    item_type_code?: string | null;
    linked_item_label?: string | null;
    impact_id?: number | null;
    cause_category_id?: number | null;
    task_count?: number;
    is_active: number;
    is_approved: number;
    created_by?: number;
    created_on?: string;
}

export interface RootCauseCreate {
    bucket: RCABucket;
    cause_description: string;
    // impact_id is REQUIRED — every cause belongs to exactly one impact (R19).
    impact_id: number;
    prev_record_id?: number | null;
    item_type_id?: number | null;
    item_record_id?: number | null;
    cause_category_id?: number | null;
}

export interface RootCauseUpdate {
    cause_description?: string;
    item_type_id?: number | null;
    item_record_id?: number | null;
    impact_id?: number | null;
    cause_category_id?: number | null;
    is_active?: number;
}

export interface CauseTaskLink {
    task_id?: number;          // when linking an existing task
    name?: string;             // when creating a new task inline
    description?: string;
    due_date?: string | null;
    doer_id?: number | null;
    validator_id?: number | null;
    approver_id?: number | null;
    priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | null;
}

export const capaRootCausesService = {
    list: async (investigationId: number, impactId?: number | null): Promise<CapaRootCause[]> => {
        const url = impactId
            ? `/api/v1/cap/investigations/${investigationId}/root-causes?impact_id=${impactId}`
            : `/api/v1/cap/investigations/${investigationId}/root-causes`;
        const resp = await apiClient.get(url);
        return resp.data?.data ?? [];
    },
    create: async (investigationId: number, data: RootCauseCreate): Promise<CapaRootCause> => {
        const resp = await apiClient.post(`/api/v1/cap/investigations/${investigationId}/root-causes`, data);
        return resp.data?.data;
    },
    update: async (investigationId: number, causeId: number, data: RootCauseUpdate): Promise<void> => {
        await apiClient.put(`/api/v1/cap/investigations/${investigationId}/root-causes/${causeId}`, data);
    },
    remove: async (investigationId: number, causeId: number): Promise<void> => {
        await apiClient.delete(`/api/v1/cap/investigations/${investigationId}/root-causes/${causeId}`);
    },
    // Per-cause action tasks
    listTasks: async (causeId: number): Promise<any[]> => {
        const resp = await apiClient.get(`/api/v1/cap/root-causes/${causeId}/tasks`);
        return resp.data?.data ?? [];
    },
    linkOrCreateTask: async (causeId: number, data: CauseTaskLink): Promise<{ task_id: number; root_cause_id: number }> => {
        const resp = await apiClient.post(`/api/v1/cap/root-causes/${causeId}/tasks`, data);
        return resp.data?.data;
    },
    unlinkTask: async (causeId: number, taskId: number): Promise<void> => {
        await apiClient.delete(`/api/v1/cap/root-causes/${causeId}/tasks/${taskId}`);
    },
};
